import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const services = [
    'Business Setup',
    'Trade License',
    'Corporate Tax & VAT',
    'Visa Services',
    'Banking Assistance',
    'Office Space',
    'Other',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Hero */}
      <section className="bg-gradient-to-r from-[#6B1B4D] to-[#2D1B3D] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Get in Touch</h1>
          <p className="text-base text-gray-200 max-w-xl mx-auto">
            Have questions about UAE business setup? We're here to help.
          </p>
        </div>
      </section>

      {/* Main Content - Side by Side Layout */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-8">
              
              {/* Left Side - Contact Info & Map */}
              <div className="lg:col-span-2 space-y-6">
                {/* Quick Contact Info */}
                <div className="bg-white rounded-xl shadow-md p-5">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="text-white" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">Phone</p>
                        <p className="text-sm text-gray-600">+971 52 866 2076</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="text-white" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">Email</p>
                        <p className="text-sm text-gray-600">setup@firmzy.in</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-white" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">Office</p>
                        <p className="text-sm text-gray-600">123 Anna Salai, Teynampet</p>
                        <p className="text-sm text-gray-600">Chennai, TN 600018</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="text-white" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">Hours</p>
                        <p className="text-sm text-gray-600">Mon-Fri: 9AM - 6PM</p>
                        <p className="text-sm text-gray-600">Sat: 9AM - 2PM</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map - Compact */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d248849.90089943527!2d80.06892495!3d13.047984899999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5265ea4f7d3361%3A0x6e61a70b6863d433!2sChennai%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1234567890" 
                    width="100%" 
                    height="200" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy"
                    title="Firmzy Office Location"
                  ></iframe>
                </div>

                {/* Why Choose Us - Quick Points */}
                <div className="bg-gradient-to-br from-[#6B1B4D] to-[#2D1B3D] rounded-xl p-5 text-white">
                  <h4 className="font-bold mb-3 text-sm">Why Choose Firmzy?</h4>
                  <div className="space-y-2">
                    {['500+ Businesses Setup', 'Expert UAE Consultants', 'Fast Processing', '24/7 Support'].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-1">Send us a Message</h2>
                  <p className="text-sm text-gray-500 mb-5">Fill out the form and we'll get back to you within 24 hours.</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-gray-700 font-medium text-sm">Full Name *</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-1 h-10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-gray-700 font-medium text-sm">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-1 h-10"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" className="text-gray-700 font-medium text-sm">Phone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+971 52 866 2076"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          className="mt-1 h-10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="service" className="text-gray-700 font-medium text-sm">Service *</Label>
                        <Select 
                          value={formData.service} 
                          onValueChange={(value) => setFormData({ ...formData, service: value })}
                        >
                          <SelectTrigger className="mt-1 h-10">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service, idx) => (
                              <SelectItem key={idx} value={service}>
                                {service}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-700 font-medium text-sm">Message *</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us about your business requirements..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        className="mt-1 min-h-[100px]"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white hover:shadow-lg transition-all h-10 px-8 text-sm group disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={16} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 group-hover:translate-x-1 transition-transform" size={16} />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
